import { refreshTokenApi } from "@/api/services/admin/auth";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { EVENTS } from "@/constants/socket";
import { logout } from "@/utils/logout";
import { ADMIN_EMAIL_KEY } from "@/utils/constants";
import { toast } from "@/utils/toast";
import Cookies from "js-cookie";
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_SERVER_URL = process.env.REACT_APP_API_HOST || "http://localhost:3000";

// Stop re-connecting after this many consecutive UNAUTHORIZED responses.
const MAX_AUTH_ATTEMPTS = 2;

const PING_INTERVAL_MS = 60000;

interface ISocketContext {
  socket: Socket | null;
}

interface SocketProviderProps {
  children: ReactNode;
}

const SocketContext = createContext<ISocketContext | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [_connected, setConnected] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const authAttemptsRef = useRef(0);

  const teardownSocket = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const connectSocket = useCallback(() => {
    const token = Cookies.get("admin-token");
    if (!token) return;

    teardownSocket();

    const socketInstance = io(SOCKET_SERVER_URL, {
      auth: { token },
      extraHeaders: { Authorization: `Bearer ${token}` },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socketInstance;

    pingIntervalRef.current = setInterval(() => {
      if (socketInstance.connected) {
        socketInstance.emit(EVENTS.GLOBAL.PING);
      }
    }, PING_INTERVAL_MS);

    socketInstance.on("connect", () => {
      // Don't set connected here - wait for GLOBAL.CONNECTED after server authentication
      console.log("Socket connected, waiting for authentication...");
    });

    socketInstance.on("disconnect", (reason) => {
      setConnected(false);
      console.log("Socket disconnected:", reason);
    });

    setSocket(socketInstance);
  }, [teardownSocket]);

  const handleUnauthorized = useCallback(async () => {
    if (authAttemptsRef.current >= MAX_AUTH_ATTEMPTS) {
      console.warn("[Socket] Max auth attempts reached, stop reconnecting");
      teardownSocket();
      return;
    }
    authAttemptsRef.current += 1;

    try {
      const email = Cookies.get(ADMIN_EMAIL_KEY);
      const refreshToken = Cookies.get("admin-refresh-token");
      if (!email || !refreshToken) {
        teardownSocket();
        logout();
        return;
      }
      await refreshTokenApi();
      connectSocket();
    } catch (error) {
      teardownSocket();
      logout();
    }
  }, [connectSocket, teardownSocket]);

  useEffect(() => {
    connectSocket();
    return teardownSocket;
  }, [connectSocket, teardownSocket]);

  useEffect(() => {
    if (!socket) return;

    socket.on(EVENTS.GLOBAL.ERROR, (message) => {
      switch (message) {
        case ERROR_STATUS_CODES.UNAUTHORIZED:
          handleUnauthorized();
          break;
        default:
          toast.error(message);
      }
    });

    socket.on(EVENTS.GLOBAL.CONNECTED, () => {
      authAttemptsRef.current = 0;
      setConnected(true);
    });

    socket.on(EVENTS.GLOBAL.PONG, () => {
      console.log("Received pong from server");
    });

    return () => {
      socket.off(EVENTS.GLOBAL.ERROR);
      socket.off(EVENTS.GLOBAL.CONNECTED);
      socket.off(EVENTS.GLOBAL.PONG);
    };
  }, [socket, handleUnauthorized]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden || socketRef.current?.connected) return;
      if (authAttemptsRef.current >= MAX_AUTH_ATTEMPTS) return;
      console.log("Tab active, reconnecting socket...");
      connectSocket();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [connectSocket]);

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};
